import { useEffect, useRef, useState } from 'react'
import { ChevronDownIcon } from '@heroicons/react/24/outline'

type Props = {
  primaryLabel: string
  applyDisabled: boolean
  applyDisabledTitle?: string
  menuDisabled?: boolean
  onApply: () => void
  onRegenerate: () => void
}

export default function BackfillApplySplitButton({
  primaryLabel,
  applyDisabled,
  applyDisabledTitle,
  menuDisabled = applyDisabled,
  onApply,
  onRegenerate,
}: Props) {
  const [menuOpen, setMenuOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!menuOpen) return

    const handlePointerDown = (event: MouseEvent) => {
      if (containerRef.current?.contains(event.target as Node)) return
      setMenuOpen(false)
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false)
    }

    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [menuOpen])

  useEffect(() => {
    if (menuDisabled) setMenuOpen(false)
  }, [menuDisabled])

  return (
    <div className="relative" ref={containerRef}>
      <div
        className={[
          'flex h-9 shrink-0 items-stretch overflow-hidden rounded-lg text-sm font-medium text-white shadow-sm',
          '[background:var(--gradient-primary)]',
          applyDisabled && menuDisabled ? 'cursor-not-allowed opacity-50' : '',
        ].join(' ')}
      >
        <button
          type="button"
          onClick={onApply}
          disabled={applyDisabled}
          title={applyDisabled ? applyDisabledTitle : undefined}
          className="inline-flex items-center gap-1.5 px-3 hover:bg-black/10 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {primaryLabel}
        </button>
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation()
            if (menuDisabled) return
            setMenuOpen((open) => !open)
          }}
          disabled={menuDisabled}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          aria-label="More apply actions"
          className="flex w-8 items-center justify-center border-l border-white/25 hover:bg-black/10 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <ChevronDownIcon
            className={`h-4 w-4 transition-transform ${menuOpen ? 'rotate-180' : ''}`}
            aria-hidden
          />
        </button>
      </div>
      {menuOpen ? (
        <div
          role="menu"
          className="absolute right-0 z-30 mt-1 min-w-[10rem] overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg"
        >
          <button
            type="button"
            role="menuitem"
            className="flex w-full items-center px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
            onClick={() => {
              setMenuOpen(false)
              onRegenerate()
            }}
          >
            Regenerate
          </button>
        </div>
      ) : null}
    </div>
  )
}
